import { z } from 'zod';
import type { GridLoadOptions } from '../../models/functions/load-grid-resource.model';
import { reportGridPresetSchema, type ReportGridPreset } from '../../models/reporting/report-grid-settings.model';
import type {
  ReportId,
  ReportingComparisonDataset,
  ReportingComparisonDatasetSelection,
  ReportingComparisonRowLevel,
  ReportingComparisonScope,
} from './enums';
import {
  ReportIds,
  ReportingComparisonDatasetSelections,
  ReportingComparisonDatasets,
  ReportingComparisonRowLevels,
  ReportingComparisonScopes,
} from './enums';

export interface ReportingApiHttpQuery {
  report_id: ReportId;
  start_date: string;
  end_date: string;
  /** Agency ids (`agencies.agency_id`). The API still accepts agency names, which older clients send. */
  mga?: string[];
  /** RMD agent ids (`p_agent_id`): each stands for the agencies that RMD covers. */
  rmd?: string[];
  title?: string;
  subtitle?: string;
  rows_per_page?: number;
  display_threshold?: number;
  /** False lists every row regardless of production; `display_threshold` is then ignored. Missing means true. */
  include_threshold?: boolean;
  ntp_display_mode?: 'hide' | 'separate' | 'general_list';
  show_suppressed_agents?: boolean;
  show_totals?: boolean;
  branding_id?: number;
  /** Which report section the request is for; see `reportsQuerySchema`. */
  scope?: ReportingComparisonScope;
}

/* Common */
export type ListSortDescriptor<T> = {
  selector: T;
  desc?: boolean;
};

export type ListFilterOperator = '=' | '<>' | '!=' | 'contains' | 'notcontains' | 'startswith' | 'endswith';
export type ListFilterValue = string | number | boolean | null;

export type ListFilterDescriptor<TSelector extends string> = [TSelector, ListFilterOperator, ListFilterValue];
export type ListFilterGroupOperator = 'and' | 'or';
export type ListFilterGroup<TSelector extends string> =
  | [ListFilterExpression<TSelector>, ListFilterGroupOperator, ListFilterExpression<TSelector>]
  | ListFilterExpression<TSelector>[];
export type ListFilterExpression<TSelector extends string> =
  | ListFilterDescriptor<TSelector>
  | ListFilterGroup<TSelector>;

const normalizeStringArrayQueryParam = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map(item => item.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  return [];
};

/**
 * Which report section a request is for. Optional only during the rollout: the server
 * treats a missing scope as `alliance_group` until every client sends one.
 */
const reportScopeQueryParam = z.nativeEnum(ReportingComparisonScopes).optional();

const optionalBooleanQuerySchema = z.preprocess(value => {
  if (value === undefined || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }

  return value;
}, z.boolean().optional());

const reportingComparisonQueryBaseSchema = z.object({
  scope: z.nativeEnum(ReportingComparisonScopes),
  current_start_date: z.coerce.date(),
  current_end_date: z.coerce.date(),
  comparison_start_date: z.coerce.date(),
  comparison_end_date: z.coerce.date(),
  top_count_mga: z.coerce.number().int().positive().max(25).default(5),
  top_count_agent: z.coerce.number().int().positive().max(25).default(5),
  minimum_premium_threshold: z.coerce.number().nonnegative().optional(),
  dataset_selection: z
    .nativeEnum(ReportingComparisonDatasetSelections)
    .default(ReportingComparisonDatasetSelections.all),
  mga: z.preprocess(normalizeStringArrayQueryParam, z.array(z.string().trim().min(1)).default([])),
  rmd: z.preprocess(normalizeStringArrayQueryParam, z.array(z.string().trim().min(1)).default([])),
});

// The range checks are written out on each schema rather than shared through a generic
// helper: wrapping in a helper turns the inferred type into `any`, which silently unpicks
// typing everywhere these DTOs are consumed.
export const reportingComparisonQuerySchema = reportingComparisonQueryBaseSchema
  .refine(value => value.current_start_date <= value.current_end_date, {
    message: 'current_start_date must be before or equal to current_end_date',
    path: ['current_start_date'],
  })
  .refine(value => value.comparison_start_date <= value.comparison_end_date, {
    message: 'comparison_start_date must be before or equal to comparison_end_date',
    path: ['comparison_start_date'],
  });

export type ReportingComparisonQueryDto = z.infer<typeof reportingComparisonQuerySchema>;

export interface ReportingComparisonHttpQuery {
  scope: ReportingComparisonScope;
  current_start_date: string;
  current_end_date: string;
  comparison_start_date: string;
  comparison_end_date: string;
  top_count_mga?: number;
  top_count_agent?: number;
  minimum_premium_threshold?: number;
  dataset_selection?: ReportingComparisonDatasetSelection;
  /** Agency ids (`agencies.agency_id`). The API still accepts agency names, which older clients send. */
  mga?: string[];
  /** RMD agent ids (`p_agent_id`): each stands for the agencies that RMD covers. */
  rmd?: string[];
}

/**
 * One comparison table's rows, asked for the way a grid asks: scope, ranges and filters from
 * the page, then `loadOptions` for the sort, the page and the total.
 *
 * `top_count_*` is absent. A top-increases table is this query sorted by premium difference
 * descending with a `take` — direction and size, which load options already carry — so the
 * page-wide `max(25)` those parameters imposed has nowhere left to live.
 */
export const reportingComparisonRowsQuerySchema = reportingComparisonQueryBaseSchema
  .omit({ top_count_mga: true, top_count_agent: true, dataset_selection: true })
  .extend({
    dataset: z.nativeEnum(ReportingComparisonDatasets),
    level: z.nativeEnum(ReportingComparisonRowLevels),
    loadOptions: z.record(z.unknown()).default({}),
  })
  .refine(value => value.current_start_date <= value.current_end_date, {
    message: 'current_start_date must be before or equal to current_end_date',
    path: ['current_start_date'],
  })
  .refine(value => value.comparison_start_date <= value.comparison_end_date, {
    message: 'comparison_start_date must be before or equal to comparison_end_date',
    path: ['comparison_start_date'],
  });

export type ReportingComparisonRowsQueryDto = z.infer<typeof reportingComparisonRowsQuerySchema>;

export interface ReportingComparisonRowsHttpBody {
  scope: ReportingComparisonScope;
  current_start_date: string;
  current_end_date: string;
  comparison_start_date: string;
  comparison_end_date: string;
  dataset: ReportingComparisonDataset;
  level: ReportingComparisonRowLevel;
  loadOptions?: GridLoadOptions;
  minimum_premium_threshold?: number;
  mga?: string[];
  rmd?: string[];
}

/**
 * How the page's tables are arranged, in the shape a preset stores it.
 *
 * Picked from the preset rather than restated, so the PDF reads exactly what a preset holds —
 * the arrangement on screen can differ from every saved preset, on purpose, and it is the one
 * on screen that is printed. The RMD and agency filter is not taken from here: it travels as
 * `mga` / `rmd`, the way every comparison request narrows, so it goes through the same checks.
 */
export const reportingComparisonArrangementSchema = reportGridPresetSchema.pick({
  hiddenColumns: true,
  grids: true,
  mgaTopCount: true,
  agentTopCount: true,
});

export type ReportingComparisonArrangementDto = z.infer<typeof reportingComparisonArrangementSchema>;

/**
 * The Comparison PDF: the page's period and filter, its arrangement, and which blocks to print.
 *
 * The API reruns every query itself from this; no figure comes from the browser. `blocks` names
 * the summary cards and charts by dataset and every table by its grid id. `time_zone` is the
 * viewer's own, so "Generated" in the footer reads in their time rather than the server's.
 */
export const reportingComparisonPdfBodySchema = reportingComparisonQueryBaseSchema
  .omit({ top_count_mga: true, top_count_agent: true, dataset_selection: true })
  .extend({
    arrangement: reportingComparisonArrangementSchema.default({}),
    blocks: z.array(z.string().trim().min(1).max(64)).min(1).max(64),
    time_zone: z.string().trim().min(1).max(64).optional(),
  })
  .refine(value => value.current_start_date <= value.current_end_date, {
    message: 'current_start_date must be before or equal to current_end_date',
    path: ['current_start_date'],
  })
  .refine(value => value.comparison_start_date <= value.comparison_end_date, {
    message: 'comparison_start_date must be before or equal to comparison_end_date',
    path: ['comparison_start_date'],
  });

export type ReportingComparisonPdfBodyDto = z.infer<typeof reportingComparisonPdfBodySchema>;

export interface ReportingComparisonPdfHttpBody {
  scope: ReportingComparisonScope;
  current_start_date: string;
  current_end_date: string;
  comparison_start_date: string;
  comparison_end_date: string;
  minimum_premium_threshold?: number;
  mga?: string[];
  rmd?: string[];
  arrangement: Pick<ReportGridPreset, 'hiddenColumns' | 'grids' | 'mgaTopCount' | 'agentTopCount'>;
  blocks: string[];
  time_zone?: string;
}

export type GetListQuery<TSortSelector extends string = string, TFilterSelector extends string = TSortSelector> = {
  loadOptions?: {
    skip?: number;
    take?: number;
    sort?: ListSortDescriptor<TSortSelector> | ListSortDescriptor<TSortSelector>[];
    filter?: ListFilterExpression<TFilterSelector>;
  };
};

export type AgencyBrandingOptionsListSortSelector = 'description' | 'value';
export type AgencyBrandingOptionsGetListPayload = {
  skip?: number;
  take?: number;
  requireTotalCount?: boolean;
  sort?:
    | ListSortDescriptor<AgencyBrandingOptionsListSortSelector>
    | ListSortDescriptor<AgencyBrandingOptionsListSortSelector>[];
  filter?: ListFilterExpression<AgencyBrandingOptionsListSortSelector>;
  scope?: ReportingComparisonScope;
};

/* Override Sessions */
export type OverrideSessionsListSortSelector = 'id' | 'subtitle' | 'period' | 'createdAt' | 'updatedAt' | 'finishedAt';
export type OverrideSessionsListFilterSelector = 'id' | 'subtitle';
export type OverrideSessionsGetListPayload = GetListQuery<
  OverrideSessionsListSortSelector,
  OverrideSessionsListFilterSelector
>;

/* Dashboard */

/**
 * Wire values for the dashboard `view` field.
 *
 * The KEYS describe what each value actually scopes to. The VALUES are the strings
 * already on the wire and must not be renamed — deployed clients send them, and a
 * released frontend would start getting rejected mid-flight.
 *
 * Read the keys, not the values: the two disagree for `MGA`, for historical reasons
 * spelled out below, and that mismatch has caused real bugs.
 */
export const DASHBOARD_VIEW = {
  /** Everything, unscoped. Requires PORTAL_ADMIN. */
  ALL: 'all',
  /** The caller's own book only. */
  PERSONAL: 'personal',
  /**
   * The caller's whole MGA — despite the value reading `'agency'`.
   *
   * `resolveScope` maps this to `agent.mgaId`, not to any agency. The MGA dashboards,
   * the PWA and summary-details all depend on that meaning, so the value is frozen;
   * the key carries the truth instead. If you want the caller's *agency*, you want
   * `DASHBOARD_LIST_VIEW.AGENCY`.
   */
  MGA: 'agency',
  /** The MGAs the caller is an RMD for. */
  RMD: 'rmd',
} as const;

/**
 * Views the grid endpoints accept, on top of the aggregate ones.
 *
 * These are deliberately absent from `DASHBOARD_VIEW`: the aggregate endpoints
 * (summary, revenue-analytics, sales-by-carriers and both revenue-projection routes)
 * have no implementation for them, and `getRevenueWeeklyByCarrier` discriminates on the
 * view with an `if` rather than a `switch` — so widening the shared type would open them
 * there with nothing to catch it at compile time.
 */
export const DASHBOARD_LIST_VIEW = {
  ...DASHBOARD_VIEW,
  /** The caller's downline: recursive, root-inclusive, derived from `manager_agent_id`. */
  TEAM: 'team',
} as const;

export type DashboardViewDto = (typeof DASHBOARD_VIEW)[keyof typeof DASHBOARD_VIEW];

export type DashboardListViewDto = (typeof DASHBOARD_LIST_VIEW)[keyof typeof DASHBOARD_LIST_VIEW];

export interface DashboardBodyDto {
  startDate?: string;
  endDate?: string;
  view?: DashboardViewDto;
  productType?: string[];
}

export interface DashboardGetListBodyDto extends Omit<DashboardBodyDto, 'view'> {
  view?: DashboardListViewDto;
  loadOptions?: Record<string, unknown>;
  /**
   * Whose team to report on, for `view: 'team'`. Ignored by every other view.
   * Server-validated against the caller's own downline — it selects a viewpoint,
   * it never widens scope.
   */
  rootAgentId?: string;
}

/** Body for the team roster and team leads endpoints. */
/**
 * Which membership the hierarchy endpoints resolve.
 *
 * `team` recurses `manager_agent_id` downward from the caller — a downline.
 * `agency` is the caller's flat agency membership: peers and upline included, which a
 * downward walk can never produce.
 *
 * The screen declares the kind; the server resolves the membership and authorises it.
 */
export type DashboardHierarchyScopeDto = 'team' | 'agency';

export interface DashboardTeamGetListBodyDto {
  startDate?: string;
  endDate?: string;
  loadOptions?: Record<string, unknown>;
  scope?: DashboardHierarchyScopeDto;
  /**
   * Whose sub-tree to render.
   *
   * Present -> the rows reachable from this agent by `manager_agent_id`, following only
   * links where BOTH ends are inside the resolved membership, with roll-ups over exactly
   * that reachable set. Anything the chain does not connect is absent: a hierarchy that
   * is not recorded is shown as missing rather than guessed at.
   *
   * Absent -> the flat membership with no roll-ups, which is what a list wants.
   *
   * Server-validated against the caller's own membership; it selects a viewpoint and can
   * never widen scope.
   */
  rootAgentId?: string;
}

/**
 * Transactions for exactly the agents a hierarchy renders.
 *
 * Takes the same `scope` + `rootAgentId` as the roster so the two resolve the identical
 * membership server-side and cannot drift. Ids are never sent for scoping.
 */
export interface DashboardTeamTransactionsBodyDto extends DashboardTeamGetListBodyDto {
  /**
   * Optional narrowing for a future tree filter — the ids still visible after it.
   * Intersected with the server-resolved membership, never unioned, so a forged or
   * stripped list cannot widen what the caller sees.
   */
  agentIds?: string[];
}

/**
 * How the year-end level is formed from settled YTD and the seasonal fraction `f`.
 * Measured by walk-forward backtest on 2015-2025 (targets 2018-2025, weeks 10-40):
 *
 *   credibility  2.25% MAPE  — YTD/f anchored on the trend prior (default)
 *   pace         4.82% MAPE  — the un-anchored YTD/f
 *   runrate      7.58% MAPE  — weekly average x 53; no seasonality at all
 *
 * `runrate` is a baseline, not a recommendation: it under-projects in 31 of 32 backtested
 * year-weeks because the year is back-loaded. It is selectable so the other two can be
 * compared against it, and every surface that offers it must show its error alongside.
 */
export type DashboardRevenueProjectionMethodDto = 'credibility' | 'pace' | 'runrate';

/** Projection endpoints only — `method` is meaningless to the other dashboard queries. */
export interface DashboardRevenueProjectionBodyDto extends DashboardBodyDto {
  method?: DashboardRevenueProjectionMethodDto;
}

export const PRODUCTION_HISTORY_HORIZONS = {
  Actual: 'actual',
  Projection: 'projection',
} as const;

export type ProductionHistoryHorizonDto =
  (typeof PRODUCTION_HISTORY_HORIZONS)[keyof typeof PRODUCTION_HISTORY_HORIZONS];

export const productionHistoryQuerySchema = z
  .object({
    /**
     * Which report section the request is for; missing means `alliance_group` until every
     * client sends one. It has to be declared here or the schema strips it, and the server's
     * access guard — which reads it off the parsed query — would read every scoped request as
     * company-wide and refuse it.
     */
    scope: reportScopeQueryParam,
    horizon: z.enum(['actual', 'projection']).default('actual'),
    method: z.enum(['runrate', 'pace', 'credibility']).optional(),
    mga: z.preprocess(normalizeStringArrayQueryParam, z.array(z.string().trim().min(1)).default([])),
    rmd: z.preprocess(normalizeStringArrayQueryParam, z.array(z.string().trim().min(1)).default([])),
  })
  .refine(value => value.horizon !== 'projection' || Boolean(value.method), {
    message: 'method is required when horizon is projection',
    path: ['method'],
  });

export type ProductionHistoryQueryDto = z.infer<typeof productionHistoryQuerySchema>;

/** The report pages that ask for filter options besides the bulletin reports, which send their report id. */
export enum ReportFilterOptionsPages {
  comparison = 'comparison',
  productionHistory = 'production_history',
}

export type ReportFilterOptionsReport = `${ReportFilterOptionsPages}` | ReportId;

/**
 * Query for `/reports/filter-options`. `report` names the page asking, because access is decided
 * per report: the viewer must be allowed that report in that scope to see its filter options.
 */
export const reportFilterOptionsQuerySchema = z.object({
  scope: z.nativeEnum(ReportingComparisonScopes),
  report: z.union([z.nativeEnum(ReportFilterOptionsPages), z.nativeEnum(ReportIds)]),
});

export type ReportFilterOptionsQueryDto = z.infer<typeof reportFilterOptionsQuerySchema>;

export interface ReportFilterOptionsHttpQuery {
  scope: ReportingComparisonScope;
  report: ReportFilterOptionsReport;
}

/**
 * Query for the production history endpoints (`/production-history/{monthly,quarterly,yearly}`).
 *
 * `horizon` is optional on the wire: the schema above defaults it to `actual`, so a client that
 * predates the Production Horizon control keeps working unchanged.
 */
export interface ProductionHistoryHttpQuery {
  scope?: ReportingComparisonScope;
  horizon?: ProductionHistoryHorizonDto;
  method?: DashboardRevenueProjectionMethodDto;
  /** Agency ids (`agencies.agency_id`). The API still accepts agency names, which older clients send. */
  mga?: string[];
  /** RMD agent ids (`p_agent_id`): each stands for the agencies that RMD covers. */
  rmd?: string[];
}
